import { useEffect, useState } from "react";
import { Store, Package, ShoppingCart, IndianRupee, GitBranch, Truck } from "lucide-react";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, PieChart, Pie, Cell } from "recharts";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { StatCard, PageHeader } from "@/components/portal/widgets";
import api, { inr } from "@/lib/api";

const PIE_COLORS = ["#eab308", "#f97316", "#0ea5e9", "#84cc16", "#ef4444"];
const stageLabel = { submitted: "Submitted", under_review: "Under Review", approved: "Approved", onboarded: "Onboarded", rejected: "Rejected" };

export default function AdminDashboard() {
  const [s, setS] = useState(null);

  useEffect(() => { api.get("/admin/stats").then((r) => setS(r.data)).catch(() => {}); }, []);

  if (!s) return <p className="text-muted-foreground">Loading dashboard…</p>;

  const pieData = Object.entries(s.pipeline).filter(([, v]) => v > 0).map(([k, v]) => ({ name: stageLabel[k], value: v }));

  return (
    <div className="animate-fade-up">
      <PageHeader title="Network Overview" subtitle="Real-time visibility across all stores and franchises." />

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <StatCard testid="stat-revenue" icon={IndianRupee} label="Total Revenue" value={inr(s.total_revenue)} accent="emerald" />
        <StatCard testid="stat-orders" icon={ShoppingCart} label="Orders" value={s.total_orders} accent="orange" />
        <StatCard testid="stat-stores" icon={Store} label="Stores" value={s.total_stores} accent="sky" />
        <StatCard testid="stat-applications" icon={GitBranch} label="Applications" value={s.total_applications} accent="violet" />
        <StatCard testid="stat-products" icon={Package} label="Products" value={s.total_products} accent="emerald" />
        <StatCard testid="stat-suppliers" icon={Truck} label="Suppliers" value={s.total_suppliers} accent="orange" />
        <StatCard testid="stat-pending" icon={GitBranch} label="Pending Reviews" value={s.pending_applications} accent="sky" />
      </div>

      <div className="grid lg:grid-cols-3 gap-6 mb-6">
        <Card className="lg:col-span-2 p-6 border-slate-200/80 dark:border-slate-800">
          <h3 className="font-heading font-semibold mb-4">Sales by Store</h3>
          <ResponsiveContainer width="100%" height={280}>
            <BarChart data={s.sales_by_store}>
              <XAxis dataKey="store" tick={{ fontSize: 11 }} interval={0} angle={-15} textAnchor="end" height={60} />
              <YAxis tick={{ fontSize: 11 }} />
              <Tooltip formatter={(v) => inr(v)} />
              <Bar dataKey="sales" fill="#eab308" radius={[6, 6, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </Card>
        <Card className="p-6 border-slate-200/80 dark:border-slate-800">
          <h3 className="font-heading font-semibold mb-4">Franchise Pipeline</h3>
          <ResponsiveContainer width="100%" height={220}>
            <PieChart>
              <Pie data={pieData} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={80} label>
                {pieData.map((e, i) => <Cell key={i} fill={PIE_COLORS[i % PIE_COLORS.length]} />)}
              </Pie>
              <Tooltip />
            </PieChart>
          </ResponsiveContainer>
        </Card>
      </div>

      <Card className="p-6 border-slate-200/80 dark:border-slate-800">
        <h3 className="font-heading font-semibold mb-4">Recent Orders</h3>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead><tr className="text-left text-muted-foreground border-b border-slate-200 dark:border-slate-800">
              <th className="py-2 pr-4">Reference</th><th className="py-2 pr-4">Customer</th><th className="py-2 pr-4">Store</th><th className="py-2 pr-4">Total</th><th className="py-2">Status</th>
            </tr></thead>
            <tbody>
              {s.recent_orders.map((o) => (
                <tr key={o.id} className="border-b border-slate-100 dark:border-slate-800/50 hover:bg-slate-50 dark:hover:bg-slate-800/50">
                  <td className="py-2.5 pr-4 font-mono text-xs">{o.reference}</td>
                  <td className="py-2.5 pr-4">{o.customer_name}</td>
                  <td className="py-2.5 pr-4 text-muted-foreground">{o.store_name}</td>
                  <td className="py-2.5 pr-4 font-semibold">{inr(o.total)}</td>
                  <td className="py-2.5"><Badge variant="secondary" className="capitalize">{o.status}</Badge></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>
    </div>
  );
}
